import * as React from "react";

import { getTranslationLabelLanguage } from "../helpers/loadTranslationLabels";
import Stack from "@mui/material/Stack";
import { Locales } from "@simplyk/common";
import { FieldError, UseFormRegister, FieldErrors, Control, Path, FieldValues, UseFormSetValue } from "react-hook-form";

import { useTranslate } from "../../../../../../../common/hooks/useTranslate";
import { TranslationCard } from "./TranslationCard";

import { isRequired } from "@/common/helpers/validators";
import { QuestionFieldFormData } from "@/common/types/customQuestion";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardSkeleton } from "@/dashboard-design-system/Skeleton/DashboardSkeleton";
import { TranslatedTextField } from "@/features/LiveFormEditor/Steps/StepAdvancedSettings/Translations/sharedComponents/TranslatedTextField";

export interface QuestionTranslationCardProps<T extends FieldValues> {
  index: number;
  control: Control<T>;
  register: UseFormRegister<T>;
  namePrefix: Path<T>;
  formLocale?: Locales;
  defaultValue: QuestionFieldFormData;
  bodyErrorMessage?: string;
  choiceErrors?: FieldErrors;
  isTranslationColumn?: boolean;
  translationLocale?: Locales;
  setValue: UseFormSetValue<T>;
  textToTranslate?: QuestionFieldFormData;
  isLoading?: boolean;
}

export function QuestionTranslationCard<T extends FieldValues>({
  index,
  control,
  register,
  defaultValue,
  bodyErrorMessage,
  choiceErrors,
  namePrefix,
  formLocale,
  isTranslationColumn,
  translationLocale,
  setValue,
  textToTranslate,
  isLoading,
}: QuestionTranslationCardProps<T>) {
  const { t } = useTranslate();
  const fieldLocale = getTranslationLabelLanguage(isTranslationColumn ? translationLocale : formLocale);

  return (
    <TranslationCard title={t("dashboard", "translation.questionNumber", { index: index + 1 })}>
      <input style={{ display: "none" }} {...register(`${namePrefix}.id` as Path<T>)} value={defaultValue.id} />
      <input
        style={{ display: "none" }}
        {...register(`${namePrefix}.questionId` as Path<T>)}
        value={defaultValue.questionId}
      />
      {isTranslationColumn && translationLocale ? (
        isLoading ? (
          <DashboardSkeleton variant="rounded" width="100%" sx={(theme) => ({ height: theme.spacing(7.5) })} />
        ) : (
          <TranslatedTextField
            label={t("dashboard", "translation.questionLabel", { lng: fieldLocale })}
            rules={[isRequired(t("common", "isRequired"))]}
            fieldName={`${namePrefix}.body` as Path<T>}
            defaultFieldValue={textToTranslate?.body}
            defaultTranslationFieldValue={defaultValue?.body}
            setValue={setValue}
            translationLocale={translationLocale}
            control={control}
            errorMessage={bodyErrorMessage}
            dataTest={`${namePrefix}.body`}
            skipTranslation={true}
          />
        )
      ) : (
        <DashboardTextField
          label={t("dashboard", "translation.questionLabel", { lng: fieldLocale })}
          rules={[isRequired(t("common", "isRequired"))]}
          control={control}
          name={`${namePrefix}.body` as Path<T>}
          defaultValue={defaultValue?.body}
          errorMessage={bodyErrorMessage}
        />
      )}

      {defaultValue?.choices && defaultValue?.choices.length > 0 && (
        <Stack gap={2}>
          {defaultValue.choices
            .slice()
            .sort((choiceA, choiceB) => choiceA.sortIndex - choiceB.sortIndex)
            .map((choice, choiceIndex) => {
              const choiceError = choiceErrors && (choiceErrors[choiceIndex] as FieldError);
              return (
                <React.Fragment key={`${choice.id}`}>
                  <input
                    style={{ display: "none" }}
                    {...register(`${namePrefix}.choices.${choiceIndex}.id` as Path<T>)}
                    value={choice.id}
                  />
                  {isTranslationColumn && translationLocale ? (
                    <TranslatedTextField
                      label={t("dashboard", "translation.optionNumber", { lng: fieldLocale, index: choiceIndex + 1 })}
                      rules={[isRequired(t("common", "isRequired"))]}
                      fieldName={`${namePrefix}.choices.${choiceIndex}.value` as Path<T>}
                      defaultFieldValue={
                        textToTranslate?.choices?.find((sourceChoice) => sourceChoice.id === choice.id)?.value
                      }
                      defaultTranslationFieldValue={choice.value}
                      setValue={setValue}
                      translationLocale={translationLocale}
                      control={control}
                      errorMessage={choiceError?.message}
                      dataTest={`${namePrefix}.choices.${choiceIndex}.value`}
                      skipTranslation={true}
                    />
                  ) : (
                    <DashboardTextField
                      label={t("dashboard", "translation.optionNumber", { lng: fieldLocale, index: choiceIndex + 1 })}
                      rules={[isRequired(t("common", "isRequired"))]}
                      control={control}
                      name={`${namePrefix}.choices.${choiceIndex}.value` as Path<T>}
                      defaultValue={choice.value}
                      errorMessage={choiceError?.message}
                    />
                  )}
                </React.Fragment>
              );
            })}
        </Stack>
      )}
    </TranslationCard>
  );
}
